import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  CancelCrossIcon,
  CloseSmallIcon,
  ConfirmDialog,
  DescriptionSmallIcon,
  IconButton,
  PauseSmallIcon,
  PlaySmallIcon,
  Tooltip,
  Trigger,
  UploadIcon,
} from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { stopwatchDelete, stopwatchPause, stopwatchResume, stopwatchStart } from "~/store/actions/actions.stopwatches";
import { isLimitedAccess } from "~/store/selectors/selector.stopwatches";
import { calculateElapsed, stopwatchMaximum } from "~/utils/date";
import { userClass } from "~/utils/utils.jsx";
import ElapsedTime from "./ElapsedTime.js";
import StopwatchDescriptionForm from "./StopwatchDescriptionForm.js";
import { showSuccessToast } from "../Toast";

const Stopwatch = ({
  id,
  elapsed,
  started_on,
  onCommit,
  parentId,
  parentType,
  projectId,
  stopwatchLarge,
  description,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(false);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [showDescriptionDialog, setShowDescriptionDialog] = useState(false);

  const isRunning = useMemo(() => started_on > 0, [started_on]);
  const isUserParentType = useMemo(() => {
    return parentType === userClass;
  }, [parentType]);

  const onResume = useCallback(() => {
    dispatch(stopwatchResume(id, started_on));
  }, [dispatch, id, started_on]);

  const onPause = useCallback(
    async (previousElapsed, nextElapsed) => {
      return dispatch(stopwatchPause(id, started_on, previousElapsed, nextElapsed));
    },
    [dispatch, id, started_on]
  );

  const onStart = useCallback(() => {
    return dispatch(stopwatchStart(parentType, parentId));
  }, [dispatch, parentId, parentType]);

  const onStopwatchMaximum = useCallback(() => {
    showSuccessToast(t("Stopwatch for this task has reached the time limit (99:59)"));
  }, [t]);

  const pause = useCallback(() => {
    const nextElapsed = calculateElapsed(started_on, elapsed);
    onPause(id, started_on, elapsed, nextElapsed);
  }, [elapsed, id, onPause, started_on]);

  const handleClick = useCallback(async () => {
    if (isRunning) {
      await pause();
    } else if (elapsed === 0 && id === 0) {
      await onStart();
    } else {
      if (elapsed >= stopwatchMaximum) {
        onStopwatchMaximum();
      } else {
        await onResume(id, started_on);
      }
    }
  }, [elapsed, id, isRunning, onResume, onStart, onStopwatchMaximum, pause, started_on]);

  const isStarted = useMemo(() => {
    return id > 0;
  }, [id]);

  const showDiscard = useMemo(() => {
    return stopwatchLarge && id > 0;
  }, [id, stopwatchLarge]);

  const removeDialog = useCallback(() => {
    if (isRunning) {
      pause();
    }
    setShowConfirmDeleteDialog(true);
  }, [isRunning, pause]);

  const exitEditMode = useCallback(() => {
    setEditMode(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isRunning) {
      const nextElapsed = calculateElapsed(started_on, elapsed);
      await onPause(id, started_on, elapsed, nextElapsed);
    }
    await onCommit(id);
  }, [elapsed, id, isRunning, onPause, onCommit, started_on]);

  const showRemoveButton = useMemo(() => !stopwatchLarge, [stopwatchLarge]);

  const enterEditMode = useCallback(() => {
    if (isRunning) {
      pause();
    }
    setEditMode(true);
  }, [isRunning, pause]);

  const resolveElapsed = useMemo(() => {
    if (isRunning) {
      return calculateElapsed(started_on, elapsed);
    }
    return elapsed;
  }, [elapsed, isRunning, started_on]);

  const handleCancelDeleteStopwatch = useCallback(() => {
    setShowConfirmDeleteDialog(false);
  }, []);

  const handleDeleteStopwatch = useCallback(() => {
    dispatch(stopwatchDelete(id));
    handleCancelDeleteStopwatch();
  }, [dispatch, handleCancelDeleteStopwatch, id]);

  const handleOpenDescriptionDialog = useCallback(() => {
    setShowDescriptionDialog(true);
  }, []);

  const handleCloseDescriptionDialog = useCallback(() => {
    setShowDescriptionDialog(false);
  }, []);

  const isLimitedAccessStopwatch = useSelector((state) => isLimitedAccess(state, projectId, parentType, parentId));

  const isSubmitDisabled = useMemo(
    () => isUserParentType || isLimitedAccessStopwatch,
    [isLimitedAccessStopwatch, isUserParentType]
  );

  const hideControls = useMemo(
    () => editMode || parentId === 0 || (stopwatchLarge && !id),
    [editMode, id, parentId, stopwatchLarge]
  );

  return (
    <div className="tw-flex tw-items-center tw-gap-1">
      <div
        className={classnames(`c-stopwatch print_hide tw-h-10 tw-p-1 tw-content-between`, {
          "is-started": isStarted,
          "is-running": isRunning,
          "tw-w-full": stopwatchLarge,
          "tw-w-[174px]": !stopwatchLarge,
        })}
      >
        <div className="c-stopwatch__controls tw-flex tw-items-center">
          <div>
            <IconButton
              onClick={handleClick}
              className={classnames(`c-stopwatch__btn-control--play tw-shadow-none`, {
                "c-stopwatch__btn-control--pause tw-pt-0.5": isRunning,
                "tw-invisible": editMode,
              })}
              variant="contained"
            >
              {isRunning ? <PauseSmallIcon /> : <PlaySmallIcon />}
            </IconButton>
          </div>

          <div className="c-stopwatch__time">
            <span className="tw-w-auto tw-shrink-0 tw-h-6 tw-flex c-stopwatch__time__duration">
              <ElapsedTime
                id={id}
                elapsed={editMode ? resolveElapsed : elapsed}
                started_on={started_on}
                exitEditMode={exitEditMode}
                onClick={enterEditMode}
                parentId={parentId}
                parentType={parentType}
              />
              {!hideControls ? (
                <Tooltip title={t("Description")}>
                  <Trigger
                    variant="text gray"
                    onClick={handleOpenDescriptionDialog}
                    className="tw-mx-1 tw-flex tw-items-center"
                  >
                    <DescriptionSmallIcon
                      className={classnames({
                        "tw-fill-theme-500 hover:tw-fill-theme-700": !description,
                        "tw-fill-theme-900": description,
                      })}
                    />
                  </Trigger>
                </Tooltip>
              ) : null}
              {showDiscard && (
                <Trigger
                  onClick={removeDialog}
                  className={classnames("st-c-btn", "st-c-btn--remove c-stopwatch__btn-remove-time", {
                    "tw-invisible": editMode,
                  })}
                  variant="text gray"
                >
                  <CloseSmallIcon />
                </Trigger>
              )}
            </span>
          </div>
          <Tooltip
            title={
              isUserParentType
                ? t("Select a project or a task first")
                : t("Manually submit this time record somewhere else and delete this stopwatch")
            }
            disable={!isSubmitDisabled}
          >
            <Trigger
              className={classnames("c-stopwatch__btn-control--submit tw-flex tw-items-center tw-p-1", {
                "tw-invisible": hideControls,
              })}
              onClick={handleSubmit}
              variant="text gray"
              disabled={isSubmitDisabled}
              data-testid="stopwatch-submit-button"
            >
              <UploadIcon
                className={classnames({
                  "tw-fill-theme-600": isSubmitDisabled,
                })}
              />
            </Trigger>
          </Tooltip>
        </div>
        {/*
          Only large stopwatch has inline alert; when stopwatch is shown elsewhere (eg. in the dropdown) the alert is
          not part of this component.
        */}
      </div>
      {showRemoveButton ? (
        <Tooltip title={t("Discard")}>
          <IconButton
            onClick={removeDialog}
            className={classnames("st-c-btn", "st-c-btn--remove", {
              "tw-invisible": editMode,
            })}
            variant="text gray"
            size="small"
          >
            <CancelCrossIcon />
          </IconButton>
        </Tooltip>
      ) : null}
      {showConfirmDeleteDialog ? (
        <ConfirmDialog
          open={showConfirmDeleteDialog}
          dialogTitle={t("Discard Time Record")}
          dialogContent={t("The time record will be deleted permanently.")}
          confirmBtnText={t("Discard")}
          cancelBtnText={t("Cancel")}
          onConfirm={handleDeleteStopwatch}
          onCancel={handleCancelDeleteStopwatch}
        />
      ) : null}
      {showDescriptionDialog ? (
        <StopwatchDescriptionForm
          id={id}
          open={showDescriptionDialog}
          description={description}
          onClose={handleCloseDescriptionDialog}
        />
      ) : null}
    </div>
  );
};

Stopwatch.propTypes = {
  id: PropTypes.number,
  elapsed: PropTypes.number,
  started_on: PropTypes.number,
  onCommit: PropTypes.func,
  parentId: PropTypes.number,
  parentType: PropTypes.string,
  projectId: PropTypes.number,
  stopwatchLarge: PropTypes.bool,
  description: PropTypes.string,
};

Stopwatch.defaultProps = {
  id: 0,
  elapsed: 0,
  started_on: null,
  onCommit: () => null,
  stopwatchLarge: false,
  description: "",
};

export default Stopwatch;
